import { io, Socket } from "socket.io-client";

let socket: Socket | null = null;

/**
 * Connect to the server's WebSocket events namespace.
 * Authenticates using the provided JWT access token.
 */
export function connectEvents(apiBaseUrl: string, token: string): Socket {
  if (socket?.connected) {
    return socket;
  }

  // Strip trailing /api/v1, /api, or /v1 to get the base origin
  const baseUrl = apiBaseUrl
    .replace(/\/api\/v1\/?$/, "")
    .replace(/\/api\/?$/, "")
    .replace(/\/v1\/?$/, "");

  socket = io(`${baseUrl}/events`, {
    auth: { token },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 2000,
  });

  return socket;
}

/**
 * Subscribe to force-logout events.
 * Calls the provided callback when the server triggers a force-logout.
 */
export function onForceLogout(callback: () => void): () => void {
  if (!socket) {
    console.warn("[events] No socket connection — call connectEvents first");
    return () => {};
  }

  const handler = () => {
    console.warn("[events] Force logout received from server");
    callback();
  };

  socket.on("force-logout", handler);

  return () => {
    socket?.off("force-logout", handler);
  };
}

/**
 * Subscribe to real-time notification events.
 * Calls the provided callback when the server pushes a new notification.
 */
export function onNotification(
  callback: (notification: {
    id: string;
    title: string;
    message: string;
    type: string;
    data?: Record<string, unknown>;
    createdAt: string;
  }) => void,
): () => void {
  if (!socket) {
    console.warn("[events] No socket connection — call connectEvents first");
    return () => {};
  }

  socket.on("notification", callback);

  return () => {
    socket?.off("notification", callback);
  };
}

/**
 * Disconnect from the WebSocket server.
 */
export function disconnectEvents() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}
